// src/utils/statisticsCharts.js
// Главный файл для монтажа дашборда с графиками

import { buildDailySeries } from "./chartUtils";
import { makeHistogram, makePie, makeLine } from "./chartRenderers";
import { makeMarksChart, makeSingleCourseChart } from "./barCharts";
import { makeStudentsAverageChart, makeSubjectsAverageChart } from "./horizontalCharts";
import { initializeModal } from "./chartModal";
import {
  loadAnalyticsData,
  loadCourses,
  loadSubjects,
  prepareMarksData,
  prepareSingleCourseData,
  prepareStudentsData,
  prepareSubjectsData,
  getUserIdFromStorage,
} from "./dataLoaders";

export function mountStatistics(root, userRole = "student") {
  if (!root) return;

  const chartRegistry = new Map(); // id -> { type, data }

  // Инициализируем модальное окно
  initializeModal(root, chartRegistry);

  // Главная функция загрузки и рендеринга
  async function loadAndRender() {
    try {
      // === Загружаем основные аналитические данные ===
      const data = await loadAnalyticsData();

      // === Рендерим основные 4 графика ===
      const hist1Card = root.querySelector("#hist1-card");
      if (hist1Card) {
        const hist1Daily = buildDailySeries(
          data.kunlik_qatnashuvchilar || [],
          { fromMonth: 9 }
        );
        makeHistogram(hist1Card, hist1Daily, chartRegistry, root);
      }

      const hist2Card = root.querySelector("#hist2-card");
      if (hist2Card) {
        const hist2Daily = buildDailySeries(
          data.yuqori_baholar || [],
          { fromMonth: 9 }
        );
        makeHistogram(hist2Card, hist2Daily, chartRegistry, root);
      }

      const pieCard = root.querySelector("#pie-card");
      if (pieCard) {
        const pieItems = (data.trenajyor_ulushi || []).map((d) => ({
          label: d.name,
          value: +d.y,
          accent: false,
        }));
        if (pieItems.length) {
          const maxIdx = pieItems.reduce(
            (m, _, i, a) => (a[i].value > a[m].value ? i : m),
            0
          );
          pieItems[maxIdx].accent = true;
        }
        makePie(pieCard, pieItems, chartRegistry, root);
      }

      const lineCard = root.querySelector("#line-card");
      if (lineCard) {
        const lineRows = (data.ortacha_baho || []).map((d) => ({
          t: new Date(d.t),
          y: +d.y,
        }));
        makeLine(lineCard, lineRows, chartRegistry, root);
      }

      // === Загружаем курсы и данные студента ===
      const courses = await loadCourses();
      const userId = getUserIdFromStorage();
      
      console.log("📊 Статистика загружена:");
      console.log("  userRole:", userRole);
      console.log("  userId:", userId);
      console.log("  courses.length:", courses?.length);

      // === Для СТУДЕНТА: рендерим диаграмму оценок по курсам ===
      if (userRole === "student") {
        console.log("👤 Загружаем для СТУДЕНТА");
        const marksCard = root.querySelector("#marks-card");
        if (marksCard) {
          const { marksData, courseDataMap } = await prepareMarksData(
            courses,
            userId
          );
          makeMarksChart(marksCard, marksData, chartRegistry, root);

          // === Настраиваем селектор для выбора одного курса ===
          const courseSelector = root.querySelector("#course-selector");
          const singleCourseCard = root.querySelector("#single-course-card");

          if (courseSelector && courses && courses.length > 0) {
            courseSelector.innerHTML = '<option value="">— Fanni tanlang —</option>';

            courses.forEach((course) => {
              const option = document.createElement("option");
              option.value = course.id;
              option.textContent = course.name;
              courseSelector.appendChild(option);
            });

            const showNoData = (svg) => {
              while (svg.firstChild) svg.removeChild(svg.firstChild);
              const NS = "http://www.w3.org/2000/svg";
              const icon = document.createElementNS(NS, "text");
              icon.setAttribute("x", "480"); icon.setAttribute("y", "185");
              icon.setAttribute("text-anchor", "middle"); icon.setAttribute("font-size", "40");
              icon.setAttribute("fill", "#d1d5db");
              icon.textContent = "📭";
              const msg = document.createElementNS(NS, "text");
              msg.setAttribute("x", "480"); msg.setAttribute("y", "240");
              msg.setAttribute("text-anchor", "middle"); msg.setAttribute("font-size", "18");
              msg.setAttribute("fill", "#9ca3af");
              msg.textContent = "Bu fan bo'yicha hali nazorat ishlari topshirilmagan";
              const hint = document.createElementNS(NS, "text");
              hint.setAttribute("x", "480"); hint.setAttribute("y", "270");
              hint.setAttribute("text-anchor", "middle"); hint.setAttribute("font-size", "14");
              hint.setAttribute("fill", "#c4c9d4");
              hint.textContent = "Nazorat ishini topshirganingizdan so'ng natijalar shu yerda ko'rinadi";
              svg.appendChild(icon);
              svg.appendChild(msg);
              svg.appendChild(hint);
            };

            const renderSingleCourse = async (courseId) => {
              if (!courseId) return;
              const svg = singleCourseCard?.querySelector(".chart svg");
              if (!courseDataMap.has(courseId)) {
                if (svg) showNoData(svg);
                return;
              }
              const singleData = await prepareSingleCourseData(courseId, courseDataMap);
              if (singleData) {
                makeSingleCourseChart(
                  singleCourseCard,
                  singleData.courseId,
                  singleData.courseName,
                  singleData.allTestsArray,
                  singleData.passedMarks,
                  singleData.totalTests,
                  chartRegistry,
                  root
                );
              } else if (svg) {
                showNoData(svg);
              }
            };

            courseSelector.addEventListener("change", (e) => {
              renderSingleCourse(parseInt(e.target.value));
            });

            // По умолчанию — первый курс с данными, иначе просто первый курс
            const firstWithData = courses.find((c) => courseDataMap.has(c.id));
            const defaultCourse = firstWithData || courses[0];
            courseSelector.value = defaultCourse.id;
            renderSingleCourse(defaultCourse.id);
          }
        }
      }

      // === Для ТРЕНЕРА И АДМИНИСТРАТОРА: рендерим диаграмму студентов ===
      if (userRole === "admin" || userRole === "trainer") {
        console.log("👨‍🏫 Загружаем для ТРЕНЕРА/АДМИНА");
        const avgCourseSelector = root.querySelector("#avg-course-selector");
        const studentsAvgCard = root.querySelector("#students-avg-card");

        console.log("🎓 Седьмая диаграмма:");
        console.log("  avgCourseSelector найден?", !!avgCourseSelector);
        console.log("  studentsAvgCard найден?", !!studentsAvgCard);
        console.log("  userRole:", userRole);

        if (avgCourseSelector && studentsAvgCard && courses && courses.length > 0) {
          console.log("✅ Все элементы найдены, начинаем заполнение");
          console.log("  coursesForAvgSelector.length:", courses.length);

          // Очищаем селектор
          avgCourseSelector.innerHTML = '<option value="">-- Predmetni tanlang --</option>';

          courses.forEach((course) => {
            console.log("  Добавляем курс:", course.id, course.name);
            const option = document.createElement("option");
            option.value = course.id;
            option.textContent = course.name;
            avgCourseSelector.appendChild(option);
          });

          console.log("✅ Селектор заполнен. Всего опций:", avgCourseSelector.children.length);

          avgCourseSelector.addEventListener("change", async (e) => {
            const selectedCourseId = parseInt(e.target.value);
            if (!selectedCourseId) {
              console.log("ℹ️ Ничего не выбрано");
              return;
            }
            console.log("📊 Выбран курс:", selectedCourseId);

            try {
              console.log("⏳ Загружаем студентов для курса", selectedCourseId);
              const studentsData = await prepareStudentsData(selectedCourseId);
              console.log("✅ Студенты загружены:", studentsData.length);
              
              const selectedCourse = courses.find((c) => c.id === selectedCourseId);
              const courseName = selectedCourse ? selectedCourse.name : `Kurs ${selectedCourseId}`;
              
              console.log("🎨 Рисуем диаграмму для", courseName);
              makeStudentsAverageChart(
                studentsAvgCard,
                selectedCourseId,
                courseName,
                studentsData,
                chartRegistry,
                root
              );
              console.log("✅ Диаграмма нарисована");
            } catch (e) {
              console.error(`❌ Ошибка загрузки студентов:`, e);
            }
          });

          // Показываем первый курс по умолчанию
          if (courses.length > 0) {
            const firstCourse = courses[0];
            console.log("📊 Загружаем первый курс по умолчанию:", firstCourse.name);

            try {
              console.log("⏳ Загружаем студентов для первого курса", firstCourse.id);
              const studentsData = await prepareStudentsData(firstCourse.id);
              console.log("✅ Студенты первого курса загружены:", studentsData.length);
              
              avgCourseSelector.value = firstCourse.id;
              
              console.log("🎨 Рисуем диаграмму для первого курса");
              makeStudentsAverageChart(
                studentsAvgCard,
                firstCourse.id,
                firstCourse.name,
                studentsData,
                chartRegistry,
                root
              );
              console.log("✅ Диаграмма первого курса нарисована");
            } catch (e) {
              console.error(`❌ Ошибка загрузки первого курса:`, e);
            }
          }
        } else {
          console.warn("⚠️ Невозможно загрузить седьмую диаграмму:");
          console.warn("  avgCourseSelector?", !!avgCourseSelector);
          console.warn("  studentsAvgCard?", !!studentsAvgCard);
          console.warn("  courses?.length > 0?", courses?.length > 0);
        }
      }

      // === Для ВСЕХ: рендерим диаграмму среднего балла по предметам ===
      const subjectsAvgCard = root.querySelector("#subjects-avg-card");
      if (subjectsAvgCard && (userRole === "admin" || userRole === "trainer")) {
        const subjects = await loadSubjects();
        const subjectsData = await prepareSubjectsData(subjects);
        makeSubjectsAverageChart(subjectsAvgCard, subjectsData, chartRegistry, root);
      }
    } catch (err) {
      console.error("Analitikani yuklashda xatolik yuz berdi:", err);
    }
  }

  loadAndRender();
}
